/**
 * QualityGateProgress Component
 *
 * Displays quality gate requirements and progress for stage transitions.
 *
 * Story FOS-4.2.3: Leaf Components Batch 1 (Stage/Quality)
 *
 * Features:
 * - Shows participant, comment, and time requirements
 * - Visual progress bars for each gate
 * - Met/unmet indicators
 * - Compact mode for inline display
 *
 * Quality Gate Requirements (from Tech Spec):
 * | Gate | Brainstorm → Refining | Refining → Ready |
 * |------|----------------------|------------------|
 * | Participants | ≥2 | ≥3 |
 * | Comments | ≥5 | ≥10 |
 * | Time | ≥24 hours | ≥48 hours total |
 *
 * @module components/governance/QualityGateProgress
 * @see AC-4.1.5.3 - Quality gate progress shows participants, comments, and time requirements
 */

import { Users, MessageSquare, Clock, Check, CheckCircle } from 'lucide-react';
import type { DiscussionStage } from '../../stores/discussionStore';

/**
 * Quality gate value with current/required tracking
 */
export interface QualityGateValue {
  current: number;
  required: number;
}

/**
 * Quality gates object for passing gate values
 */
export interface QualityGates {
  participants: QualityGateValue;
  comments: QualityGateValue;
  timeElapsed: QualityGateValue;
}

/**
 * Props for QualityGateProgress component
 */
export interface QualityGateProgressProps {
  /** Current discussion stage */
  stage: DiscussionStage;
  /** Quality gates object (preferred) */
  gates?: QualityGates;
  /** Number of participants in the discussion (legacy) */
  participantCount?: number;
  /** Number of comments in the discussion (legacy) */
  commentCount?: number;
  /** Timestamp when discussion started (legacy) */
  startedAt?: number;
  /** Whether to show compact view */
  compact?: boolean;
  /** Optional class name */
  className?: string;
}

/**
 * Gate requirements by stage
 */
const GATE_REQUIREMENTS = {
  Brainstorm: {
    participants: 2,
    comments: 5,
    hours: 24,
  },
  Refining: {
    participants: 3,
    comments: 10,
    hours: 48,
  },
  Ready: {
    participants: 3,
    comments: 10,
    hours: 48,
  },
} as const;

/**
 * Calculate hours elapsed since start
 */
function getHoursElapsed(startedAt: number): number {
  return Math.floor((Date.now() - startedAt) / (1000 * 60 * 60));
}

/**
 * Format hours for display
 */
function formatHours(hours: number): string {
  if (hours < 1) {
    return '<1h';
  } else if (hours < 24) {
    return `${hours}h`;
  } else {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
}

/**
 * Calculate progress percentage (capped at 100)
 */
function getProgressPercent(current: number, required: number): number {
  return Math.min(100, Math.round((current / required) * 100));
}

/**
 * Single gate progress component
 */
function GateProgress({
  icon: Icon,
  label,
  current,
  required,
  unit,
  testId,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  current: number;
  required: number;
  unit: string;
  testId: string;
}) {
  const isMet = current >= required;
  const progress = getProgressPercent(current, required);

  return (
    <div
      className={`flex flex-col gap-1.5 p-3 rounded-lg border ${
        isMet
          ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
          : 'bg-muted/50 border-border'
      }`}
      data-testid={testId}
      data-met={isMet.toString()}
      aria-label={`${label} requirement: ${Math.floor(current)} of ${required} ${unit}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${isMet ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
          <span className={`text-sm font-medium ${isMet ? 'text-green-700 dark:text-green-300' : 'text-foreground'}`}>
            {label}
          </span>
        </div>
        {isMet && <Check className="h-4 w-4 text-green-600 dark:text-green-400" />}
      </div>

      {/* Progress text */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {Math.floor(current)} / {required} {unit}
        </span>
        <span className={isMet ? 'text-green-600 dark:text-green-400 font-medium' : 'text-muted-foreground'}>
          {progress}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            isMet ? 'bg-green-500' : 'bg-primary'
          }`}
          style={{ width: `${progress}%` }}
          data-testid="progress-bar"
        />
      </div>
    </div>
  );
}

/**
 * Compact gate indicator
 */
function CompactGate({
  icon: Icon,
  isMet,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  isMet: boolean;
  label: string;
}) {
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
        isMet
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
          : 'bg-muted text-muted-foreground'
      }`}
      title={label}
    >
      <Icon className="h-3 w-3" />
      {isMet && <Check className="h-3 w-3" data-testid="gate-check" />}
    </div>
  );
}

/**
 * QualityGateProgress - Displays gate requirements and progress
 *
 * @example
 * ```tsx
 * // Full view
 * <QualityGateProgress
 *   stage="Brainstorm"
 *   participantCount={3}
 *   commentCount={8}
 *   startedAt={Date.now() - 48 * 3600000}
 * />
 *
 * // Compact view
 * <QualityGateProgress
 *   stage="Brainstorm"
 *   participantCount={3}
 *   commentCount={8}
 *   startedAt={Date.now() - 48 * 3600000}
 *   compact={true}
 * />
 * ```
 */
export function QualityGateProgress({
  stage,
  gates,
  participantCount: legacyParticipantCount,
  commentCount: legacyCommentCount,
  startedAt,
  compact = false,
  className = '',
}: QualityGateProgressProps) {
  const requirements = GATE_REQUIREMENTS[stage];

  // Support both gates object (preferred) and legacy individual props
  const participantCount = gates?.participants.current ?? legacyParticipantCount ?? 0;
  const commentCount = gates?.comments.current ?? legacyCommentCount ?? 0;
  const hoursElapsed = gates?.timeElapsed.current ?? (startedAt ? getHoursElapsed(startedAt) : 0);

  const participantsMet = participantCount >= requirements.participants;
  const commentsMet = commentCount >= requirements.comments;
  const timeMet = hoursElapsed >= requirements.hours;
  const allMet = participantsMet && commentsMet && timeMet;

  // Ready stage shows completion message
  if (stage === 'Ready') {
    return (
      <div
        className={`flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 ${className}`}
        data-testid="all-gates-complete"
      >
        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
        <span className="text-sm font-medium text-green-700 dark:text-green-300">
          All quality gates passed - ready for proposal extraction
        </span>
      </div>
    );
  }

  // Compact view
  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 ${className}`}
        data-testid="quality-gate-compact"
      >
        <CompactGate icon={Users} isMet={participantsMet} label={`${participantCount}/${requirements.participants} participants`} />
        <CompactGate icon={MessageSquare} isMet={commentsMet} label={`${commentCount}/${requirements.comments} comments`} />
        <CompactGate icon={Clock} isMet={timeMet} label={`${formatHours(hoursElapsed)}/${requirements.hours}h elapsed`} />
      </div>
    );
  }

  // Full view
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-foreground">
          Requirements for {stage === 'Brainstorm' ? 'Refining' : 'Ready'}
        </h4>
        {allMet && (
          <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
            <Check className="h-3 w-3" />
            All met
          </span>
        )}
      </div>

      {/* Gates */}
      <div className="grid gap-3 sm:grid-cols-3">
        <GateProgress
          icon={Users}
          label="Participants"
          current={participantCount}
          required={requirements.participants}
          unit="participants"
          testId="gate-participants"
        />
        <GateProgress
          icon={MessageSquare}
          label="Comments"
          current={commentCount}
          required={requirements.comments}
          unit="comments"
          testId="gate-comments"
        />
        <GateProgress
          icon={Clock}
          label="Time"
          current={hoursElapsed}
          required={requirements.hours}
          unit="hours"
          testId="gate-time"
        />
      </div>
    </div>
  );
}

export default QualityGateProgress;
